import {
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  KPA_DEADLINE,
  PB,
  allOf,
  exemptWhen,
  investorSection,
  localPlanField,
  otherLawsAttachment,
  propertySection,
  rightToDisposeAttachment,
  rightToDisposeSection,
  stampDutyExemptionFields,
  zoningDecisionAttachment,
} from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * F7. Wniosek o zmianę pozwolenia na budowę (PB-7) albo o przeniesienie decyzji o pozwoleniu na budowę (PB-9) — art. 36a
 * ust. 1 i 1b oraz art. 40 ust. 1, 1a i 5 ustawy Prawo budowlane (t.j. Dz. U. 2025 poz. 418, tekst
 * https://api.sejm.gov.pl/eli/acts/DU/2025/418/text.pdf — pobrany 2026-10-22). Wzory GUNB (wersja 2026): PB-7
 * https://www.gov.pl/attachment/259a89d0-e1cd-4280-8945-bf7a05aa785c, PB-9
 * https://www.gov.pl/attachment/324ac41f-320b-4d91-ab51-58d094e14332 — pobrane 2026-10-22 (DOCX): dane decyzji (data,
 * numer, znak, nazwa zamierzenia), dotychczasowy inwestor i oświadczenie o przejęciu warunków (PB-9). Zgoda dotychczasowego
 * inwestora (podpisuje ją druga strona) jako załącznik albo — przy przejściu własności po wydaniu pozwolenia (art. 40
 * ust. 1a) — dokument przejścia. Opłata PB-9 90 zł (część III ust. 11 załącznika do ustawy o opłacie skarbowej — według
 * katalogu), PB-7 według stawek za pozwolenie na budowę w zakresie zmiany. Spoza wzoru: dane nieruchomości (potrzebne do
 * oświadczenia PB-5 składanego w formularzu) i opcjonalny opis zmian.
 */
const CHANGE: EbiCondition = { field: "rodzajWniosku", equals: "pb7" };
const TRANSFER: EbiCondition = { field: "rodzajWniosku", equals: "pb9" };
const CHOSEN: EbiCondition = { field: "rodzajWniosku", filled: true };
const OWNERSHIP_PASSED: EbiCondition = { field: "przejscieWlasnosci", equals: "tak" };

const F7: IEbiFormDefinition = {
  code: "F7",
  version: 1,
  title: "Wniosek o zmianę albo przeniesienie pozwolenia na budowę",
  category: EEbiFormCategory.Construction,
  office: EEbiFormOffice.County,
  description: "Zmiana decyzji o pozwoleniu na budowę (PB-7) albo przeniesienie jej na nowego inwestora (PB-9).",
  keywords: ["zmiana pozwolenia", "PB-7", "przeniesienie pozwolenia", "PB-9", "nowy inwestor", "projekt zamienny"],
  organ: "Organ, który wydał decyzję o pozwoleniu na budowę (starosta albo prezydent miasta na prawach powiatu)",
  legalBasis: `art. 36a ust. 1 i 1b (zmiana pozwolenia) oraz art. 40 ust. 1, 1a i 5 (przeniesienie decyzji) ${PB}`,
  request: "Wnoszę o zmianę albo przeniesienie decyzji o pozwoleniu na budowę wskazanej we wniosku.",
  feeLabel: "wg stawek",
  fee: "Zmiana pozwolenia (PB-7) — opłata skarbowa według stawek za pozwolenie na budowę w zakresie zmiany; przeniesienie decyzji (PB-9) — 90 zł; sprawy budownictwa mieszkaniowego zwolnione; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do miesiąca",
  deadline: KPA_DEADLINE,
  variantField: "rodzajWniosku",
  variants: {
    pb7: { title: "Wniosek o zmianę pozwolenia na budowę (PB-7)" },
    pb9: { title: "Wniosek o przeniesienie decyzji o pozwoleniu na budowę (PB-9)" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj wniosku",
      fields: [
        {
          code: "rodzajWniosku",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "pb7", label: "zmianę pozwolenia na budowę (PB-7)" },
            { value: "pb9", label: "przeniesienie decyzji o pozwoleniu na budowę na nowego inwestora (PB-9)" },
          ],
        },
      ],
    },
    applicantSection(),
    representationSection(),
    investorSection({
      description:
        "Inwestorem (przy PB-9 — nowym inwestorem) jest wnioskodawca, a gdy wniosek składa pełnomocnik — mocodawca. Dane kolejnych inwestorów dodaje się we wniosku.",
    }),
    {
      code: "dotychczasowyInwestor",
      title: "Dane dotychczasowego inwestora",
      visibleWhen: TRANSFER,
      fields: [
        { code: "poprzedniInwestorNazwa", type: "text", label: "Imię i nazwisko lub nazwa", required: true, maxLength: 300 },
        { code: "poprzedniInwestorAdres", type: "text", label: "Adres — ulica, numer, kod pocztowy, miejscowość", required: true, maxLength: 300 },
      ],
    },
    {
      code: "decyzja",
      title: "Informacje o decyzji o pozwoleniu na budowę",
      description: "Wniosek rozpatruje organ, który wydał decyzję o pozwoleniu na budowę.",
      visibleWhen: CHOSEN,
      fields: [
        { code: "decyzjaData", type: "date", label: "Data wydania", required: true, width: "half" },
        { code: "decyzjaNumer", type: "text", label: "Nr decyzji", required: true, width: "half", maxLength: 100 },
        { code: "decyzjaZnak", type: "text", label: "Znak sprawy", width: "half", maxLength: 100 },
        { code: "zamierzenieNazwa", type: "text", label: "Nazwa zamierzenia budowlanego objętego decyzją", required: true, maxLength: 500 },
      ],
    },
    propertySection("Dane nieruchomości objętej decyzją (do oświadczenia PB-5)", CHOSEN),
    {
      code: "zmiana",
      title: "Zmiana pozwolenia",
      visibleWhen: CHANGE,
      fields: [
        { code: "opisZmian", type: "textarea", label: "Zakres wnioskowanych zmian (nieobowiązkowo)", maxLength: 3000 },
        localPlanField(),
      ],
    },
    {
      code: "przejecie",
      title: "Oświadczenie nowego inwestora",
      visibleWhen: TRANSFER,
      fields: [
        {
          code: "przejecieWarunkow",
          type: "declaration",
          label: "Przejmuję na siebie warunki zawarte w decyzji o pozwoleniu na budowę wskazanej we wniosku.",
          required: true,
        },
        {
          code: "przejscieWlasnosci",
          type: "radio",
          label:
            "Czy własność nieruchomości albo użytkowanie wieczyste objęte pozwoleniem przeszły z dotychczasowego inwestora na nowego inwestora po wydaniu pozwolenia (art. 40 ust. 1a)?",
          required: true,
          options: YES_NO,
          hint: "Jeśli tak — zgoda dotychczasowego inwestora nie jest wymagana.",
        },
      ],
    },
    rightToDisposeSection(CHOSEN),
    {
      code: "oplata",
      title: "Opłata skarbowa",
      visibleWhen: CHOSEN,
      fields: [
        {
          ...feeInfo(
            "infoOplata",
            "Opłata skarbowa za zmianę pozwolenia odpowiada stawkom za pozwolenie na budowę w zakresie zmiany — urząd potwierdzi kwotę. Sprawy budownictwa mieszkaniowego są zwolnione z opłaty.",
          ),
          visibleWhen: CHANGE,
        },
        ...stampDutyExemptionFields(),
        {
          code: "oplataPrzeniesienie",
          type: "fee",
          label: "Opłata skarbowa za przeniesienie decyzji o pozwoleniu na budowę",
          visibleWhen: TRANSFER,
          feeAmount: 90,
          feeWaivedWhen: exemptWhen(),
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "projektZmiany",
      label: "Projekt zagospodarowania działki lub terenu oraz projekt architektoniczno-budowlany z naniesionymi zmianami",
      hint: "Wraz z opiniami, uzgodnieniami i pozwoleniami w zakresie zmiany — pliki podpisane przez projektanta.",
      multiple: true,
      visibleWhen: CHANGE,
      required: true,
    },
    zoningDecisionAttachment(CHANGE),
    otherLawsAttachment("Np. decyzja o środowiskowych uwarunkowaniach, pozwolenie konserwatorskie — w zakresie zmiany.", CHANGE),
    {
      code: "zgodaInwestora",
      label: "Zgoda dotychczasowego inwestora, na rzecz którego decyzja została wydana",
      hint: "Podpisana przez dotychczasowego inwestora; zamiast oryginału można dołączyć kopię (art. 40 ust. 1 pkt 2).",
      visibleWhen: allOf(TRANSFER, { field: "przejscieWlasnosci", equals: "nie" }),
      required: true,
    },
    {
      code: "dokumentPrzejscia",
      label: "Dokument potwierdzający przejście własności lub użytkowania wieczystego na nowego inwestora",
      hint: "Np. akt notarialny albo odpis z księgi wieczystej (art. 40 ust. 1a).",
      visibleWhen: allOf(TRANSFER, OWNERSHIP_PASSED),
      required: true,
    },
    {
      code: "kopiaDecyzji",
      label: "Kopia decyzji o pozwoleniu na budowę",
      hint: "(praktyka urzędów) — nieobowiązkowo.",
      visibleWhen: CHOSEN,
    },
    rightToDisposeAttachment(CHOSEN),
    ...proxyAttachments(),
    feeProofAttachment("Potwierdzenie uiszczenia opłaty skarbowej", { visibleWhen: allOf(CHOSEN, { not: exemptWhen() }) }),
    otherAttachments(),
  ],
};

export default F7;
